/**
 * Hook for fetching and managing candidate profile data
 * Uses the /me endpoint which extracts user from JWT token
 * Automatically fetches from API and caches in sessionStorage
 */

import { useState, useEffect, useCallback } from 'react';
import { candidateServiceApi, CandidateProfile } from '../services/candidateServiceApi';
import { SessionCache } from '../utils/sessionCache';

interface UseCandidateProfileResult {
    profile: CandidateProfile | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
    clearCache: () => void;
}

export const useCandidateProfile = (): UseCandidateProfileResult => {
    const [profile, setProfile] = useState<CandidateProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // First check if we have cached data
            const cachedProfile = SessionCache.getCandidateProfile();
            if (cachedProfile) {
                console.log('Using cached profile data');
                setProfile(cachedProfile);
                setLoading(false);
                return;
            }

            // Fetch from API using /me endpoint (no ID needed, uses JWT)
            console.log('Fetching candidate profile from API...');
            const response = await candidateServiceApi.getCandidateProfile();

            if (response.success && response.candidate) {
                // Cache the profile
                SessionCache.setCandidateProfile(response.candidate);
                setProfile(response.candidate);
            } else {
                setError(response.error || 'Failed to fetch profile');
            }
        } catch (err: any) {
            console.error('Error fetching candidate profile:', err);
            setError(err.response?.data?.message || 'Failed to fetch candidate profile');
        } finally {
            setLoading(false);
        }
    }, []);

    const refetch = useCallback(async () => {
        // Clear cache before refetching
        SessionCache.clearCandidateProfile();
        await fetchProfile();
    }, [fetchProfile]);

    const clearCache = useCallback(() => {
        SessionCache.clearCandidateProfile();
        setProfile(null);
    }, []);

    useEffect(() => {
        // Check if user is authenticated before fetching
        const token = localStorage.getItem('authToken');
        if (token) {
            fetchProfile();
        } else {
            setLoading(false);
            setError('Not authenticated');
        }
    }, [fetchProfile]);

    return {
        profile,
        loading,
        error,
        refetch,
        clearCache,
    };
};
